import {
  useGetCurrentUserQuery,
  useGetRecordingQuery,
} from '@carboncopyco/data';
import {
  ArchiveIcon,
  CopyOutlineIcon,
  DuplicateOutlineIcon,
  LinkIcon,
  PlayCircleOutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import {
  Dropdown,
  DropdownItem,
  InlineLink,
  Separator,
} from '@carboncopyco/ui';
import { useCloneAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCopyUrl } from '../../../hooks';
import {
  ContinueCloningKBLink,
  ContinueCloningMenuItem,
} from '../../ContinueCloning';
import { useClonePageProps } from '../../InfoPage/ClonePage/ClonePageContext';
import { usePermissionsContext } from '../../utils';
import { RecorderStudioDropdownAction } from './actions';
import {
  ArchiveCloneModal,
  CopyCloneModalWithFolders,
  DuplicateCloneModal,
  MoveCloneModal,
} from './modals';
import type { CloneActionsDropdownContainerProps } from './types';

export const CloneActionsDropdownContainer = ({
  triggerElement,
  arrow,
  side,
  align,
  organizationId,
  cloneId,
  workspaceId,
  goToClone,
  continueCloningProps,
  handleClonePreview,
  goToCopiedClone,
  open,
  onOpenChange,
  goToStudio,
  goToWorkspacePage,
  goToFolderPage,
  dropdownContent: DropdownContent,
}: CloneActionsDropdownContainerProps) => {
  const {
    continueCloningProps: { ldFlag },
  } = useClonePageProps();
  const flags = useFlags();
  const { copyCurrentURL } = useCopyUrl();
  const { data: clone } = useGetRecordingQuery({
    organizationId,
    id: cloneId,
  });
  const { trackCloneClickArchive } = useCloneAnalytics({
    location: 'clone_info_page',
    cloneId: clone?.id,
  });
  const { data: userResponse } = useGetCurrentUserQuery();
  const { user } = userResponse || {};
  const adminPermission = user?.adminPermission;
  const hasStudioAccess = adminPermission?.recorderStudioAccess;
  const { getPermissions } = usePermissionsContext();

  const duplicateClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.duplicateRecord
  );

  const copyToWorkspacePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.copyRecord
  );
  const deleteClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.deleteRecordingById
  );

  const continueCloningPermission = getPermissions(
    PermissionKey.draft,
    PermissionAction.createARecordingDraft
  );
  const moveClonePermission = getPermissions(
    PermissionKey.record,
    PermissionAction.moveRecord
  );

  const [
    showDuplicateCloneModal,
    {
      setTrue: setTrueShowDuplicateCloneModal,
      setFalse: setFalseShowDuplicateCloneModal,
    },
  ] = useBoolean(false);
  const [
    showCopyCloneModal,
    {
      setTrue: setTruesShowCopyCloneModal,
      setFalse: setFalseShowCopyCloneModal,
    },
  ] = useBoolean(false);
  const [
    showArchiveCloneModal,
    {
      setTrue: setTrueShowArchiveCloneModal,
      setFalse: setFalseShowArchiveCloneModal,
    },
  ] = useBoolean(false);
  const [showMoveCloneModal, setShowMoveCloneModal] = useBoolean(false);

  const cloneFolderId = String(clone?.folderId);

  return (
    <>
      <Dropdown
        asChild
        triggerElement={triggerElement}
        {...{ arrow, side, align, open, onOpenChange }}
      >
        <DropdownContent
          previewClone={
            clone && (
              <DropdownItem
                iconLeft={<ShowOutlineIcon />}
                label="Preview"
                onClick={(event) => {
                  event.preventDefault();
                  handleClonePreview(clone);
                }}
              />
            )
          }
          duplicateClone={
            duplicateClonePermission && (
              <DropdownItem
                iconLeft={<DuplicateOutlineIcon size={18} />}
                label="Duplicate"
                onClick={setTrueShowDuplicateCloneModal}
              />
            )
          }
          moveClone={
            moveClonePermission && (
              <DropdownItem
                iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
                label="Move to..."
                onClick={setShowMoveCloneModal.setTrue}
              />
            )
          }
          copyClone={
            copyToWorkspacePermission && (
              <DropdownItem
                iconLeft={<CopyOutlineIcon size={18} />}
                label="Copy to..."
                onClick={setTruesShowCopyCloneModal}
              />
            )
          }
          copyUrl={
            flags.copyUrl && (
              <DropdownItem
                iconLeft={<LinkIcon size={18} />}
                label="Copy page URL"
                onClick={() => {
                  copyCurrentURL();
                }}
              />
            )
          }
          continueCloning={
            continueCloningPermission &&
            !!ldFlag &&
            typeof continueCloningProps?.onSelect === 'function' && (
              <ContinueCloningMenuItem
                {...continueCloningProps}
                learnMore={
                  <InlineLink
                    href={ContinueCloningKBLink}
                    label="Learn more"
                    className="ml-1.5 inline-flex"
                    target="_blank"
                  />
                }
              />
            )
          }
          playVideo={
            flags.cloneScreenRecordingPlayButton &&
            clone?.video && (
              <DropdownItem
                iconLeft={<PlayCircleOutlineIcon />}
                label="Session playback"
                onClick={() => {
                  if (window.dsDesktop) {
                    window.dsDesktop.shellOpenExternal(clone.video);
                  } else {
                    window.open(clone.video, '_blank');
                  }
                }}
              />
            )
          }
          archiveClone={
            deleteClonePermission && (
              <DropdownItem
                iconLeft={<ArchiveIcon size={18} />}
                label="Archive"
                onClick={() => {
                  trackCloneClickArchive();
                  setTrueShowArchiveCloneModal();
                }}
              />
            )
          }
          openInStudio={
            hasStudioAccess && (
              <>
                <div className="py-2">
                  <Separator horizontal />
                </div>
                <RecorderStudioDropdownAction
                  organizationId={organizationId}
                  cloneId={cloneId}
                  goToStudio={goToStudio}
                />
              </>
            )
          }
        />
      </Dropdown>
      {showDuplicateCloneModal && (
        <DuplicateCloneModal
          organizationId={organizationId}
          cloneId={cloneId}
          onClose={setFalseShowDuplicateCloneModal}
          show={showDuplicateCloneModal}
          goToDuplicatedClone={goToClone}
        />
      )}
      {showCopyCloneModal && (
        <CopyCloneModalWithFolders
          organizationId={organizationId}
          clone={clone}
          onClose={setFalseShowCopyCloneModal}
          goToCopiedClone={goToCopiedClone}
        />
      )}
      {showArchiveCloneModal && (
        <ArchiveCloneModal
          folderId={cloneFolderId}
          organizationId={organizationId}
          cloneId={cloneId}
          show={showArchiveCloneModal}
          onClose={setFalseShowArchiveCloneModal}
          navigateOnSuccess={() =>
            cloneFolderId === '0'
              ? goToWorkspacePage(workspaceId)
              : goToFolderPage(cloneFolderId, workspaceId)
          }
        />
      )}
      {showMoveCloneModal && (
        <MoveCloneModal
          clone={clone}
          onClose={setShowMoveCloneModal.setFalse}
          workspaceId={workspaceId}
          organizationId={organizationId}
        />
      )}
    </>
  );
};
